import React, { useState, useRef, useEffect } from "react";
import { useGlobalState } from "./Context/GlobalStateContext";
import { Modal } from "react-bootstrap";
import { showErrorMessage, showSuccessMessage } from "./Shared/Toast";

interface TextEditorProps {
  value: string;
  onChange: (val: string) => void;
}

const QuillEditor: React.FC<TextEditorProps> = ({ value, onChange }) => {
  const [isSource, setIsSource] = useState(false);
  const [sourceValue, setSourceValue] = useState(value);
  const editorRef = useRef<HTMLDivElement>(null);
  const globalState = useGlobalState();
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [tempTextColor, setTempTextColor] = useState("#000000");
  const [tempBgColor, setTempBgColor] = useState("#ffffff");
  const [showEmojiModal, setShowEmojiModal] = useState(false);
  const [savedRange, setSavedRange] = useState<Range | null>(null);
  const [selectedFont, setSelectedFont] = useState("Arial");
  const [imageWidth, setImageWidth] = useState("");
  const [imageHeight, setImageHeight] = useState("");

  const checkValidation = (type: string) => {
    showSuccessMessage(
      type === "video"
        ? "Video inserted successfully!"
        : type === "image"
        ? "Image inserted successfully!"
        : "Link inserted successfully!"
    );
  };

  const handleToggleSource = () => {
    if (!isSource && editorRef.current) {
      setSourceValue(editorRef.current.innerHTML);
    } else if (isSource && editorRef.current) {
      editorRef.current.innerHTML = sourceValue;
      onChange(sourceValue);
    }
    setIsSource((prev) => !prev);
  };

  const handleFormat = (command: string, value?: string) => {
    if (isSource || !editorRef.current) return;
    document.execCommand(command, false, value);
    onChange(editorRef.current.innerHTML);
  };

  const handleTextColorChange = (color: string) => {
    if (editorRef.current) {
      document.execCommand("foreColor", false, color);
      onChange(editorRef.current.innerHTML);
    }
    setShowTextColorPicker(false);
  };
  const handleBgColorChange = (color: string) => {
    if (editorRef.current) {
      document.execCommand("hiliteColor", false, color);
      onChange(editorRef.current.innerHTML);
    }
    setShowBgColorPicker(false);
  };

  const handleInsertEmoji = (emoji: string) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const selection = window.getSelection();
    if (savedRange) {
      selection?.removeAllRanges();
      selection?.addRange(savedRange);
    }
    const range = selection?.getRangeAt(0);
    if (!range) return;
    const textNode = document.createTextNode(emoji);
    range.insertNode(textNode);

    const newRange = document.createRange();
    newRange.setStartAfter(textNode);
    newRange.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(newRange);

    setTimeout(() => {
      onChange(editorRef.current!.innerHTML);
    }, 0);

    setShowEmojiModal(false);
  };

  const handleFontChange = (font: string) => {
    if (isSource || !editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    editorRef.current.focus();

    if (!range.collapsed) {
      document.execCommand("fontName", false, font);
    } else {
      const span = document.createElement("span");
      span.style.fontFamily = font;
      span.appendChild(document.createTextNode("\u200B"));
      range.insertNode(span);

      const newRange = document.createRange();
      newRange.setStart(span.firstChild as Node, 1);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    setSelectedFont(font);
    setTimeout(() => {
      if (editorRef.current) onChange(editorRef.current.innerHTML);
    }, 0);
  };

  const insertImage = () => setShowImageModal(true);
  const handleInsertImage = () => {
    if (!imageUrl.trim()) {
      showErrorMessage("Please enter Image URL!");
      return;
    }

    const width = imageWidth.trim() || "300";
    const height = imageHeight.trim() || "200";

    if (editorRef.current) {
      editorRef.current.focus();
      const html = `
      <div style="margin:8px 0;">
        <img 
          src="${imageUrl}" 
          alt="Inserted Image" 
          width="${width}" 
          height="${height}"
          style="object-fit:cover;display:block;margin-bottom:4px;border-radius:6px;"
        /> 
      </div>`;
      document.execCommand("insertHTML", false, html);
      onChange(editorRef.current.innerHTML);
    }

    checkValidation("image");
    setShowImageModal(false);
    setImageUrl("");
    setImageWidth("");
    setImageHeight("");
  };

  const insertVideo = () => {
    setShowVideoModal(true);
  };

  const handleInsertVideo = () => {
    if (videoUrl.trim() === "") {
      showErrorMessage("Please Enter Video URL !");
      return;
    }

    if (editorRef.current) {
      editorRef.current.focus();
      const videoHTML = `
            <div style="margin:8px 0;">
                <iframe 
                    src="${videoUrl}" 
                    width="300" 
                    height="200" 
                    allowfullscreen 
                    style="border:0;display:block;margin-bottom:4px;">
                </iframe>
            </div>
        `;
      document.execCommand("insertHTML", false, videoHTML);
      onChange(editorRef.current.innerHTML);
    }

    checkValidation("video");
    setShowVideoModal(false);
    setVideoUrl("");
  };

  const insertSymbol = (symbol: string) => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(symbol));
    onChange(editorRef.current.innerHTML);
  };

  const handleInsertLink = () => {
    if (linkUrl.trim() === "" || linkText.trim() === "") {
      showErrorMessage("Please enter both link URL and text!");
      return;
    }

    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(
        "insertHTML",
        false,
        `<a href="${linkUrl}" target="_blank" style="color:#007bff; text-decoration:underline;">${linkText}</a>`
      );
      onChange(editorRef.current.innerHTML);
    }

    checkValidation("link");
    setShowLinkModal(false);
    setLinkUrl("");
    setLinkText("");
  };

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.style.textAlign = "left";
    }
    if (
      !isSource &&
      editorRef.current &&
      document.activeElement !== editorRef.current
    ) {
      const currentContent = editorRef.current.innerHTML;
      if (currentContent !== value) editorRef.current.innerHTML = value;
    }
  }, [value, isSource]);

  const handleInput = (e?: React.FormEvent<HTMLDivElement>) => {
    if (!editorRef.current) return;
    const container = editorRef.current;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const cursorNode = range.startContainer;

    if (e && (e.nativeEvent as InputEvent).inputType === "insertParagraph") {
      e.preventDefault();

      const newLine = document.createElement("div");
      const br = document.createElement("br");
      newLine.appendChild(br);

      let parentLine = cursorNode;
      while (
        parentLine &&
        parentLine.nodeName !== "DIV" &&
        parentLine !== container
      ) {
        parentLine = parentLine.parentNode as Node;
      }

      if (parentLine && parentLine !== container && parentLine.nextSibling) {
        container.insertBefore(newLine, parentLine.nextSibling);
      } else {
        container.appendChild(newLine);
      }

      const newRange = document.createRange();
      newRange.setStart(newLine, 0);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);

      onChange(container.innerHTML);
      return;
    }

    let html = container.innerHTML;
    const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;
    const plainHTML = html.replace(/<a [^>]*>(.*?)<\/a>/gi, "$1");
    const linkedHTML = plainHTML.replace(
      urlRegex,
      (url) =>
        `<a href="${
          url.startsWith("http") ? url : `https://${url}`
        }" target="_blank" style="color:purple; text-decoration:underline;">${url}</a>`
    );

    if (linkedHTML !== container.innerHTML) {
      const pos = window.getSelection()?.getRangeAt(0);
      container.innerHTML = linkedHTML;

      if (pos) {
        const range = document.createRange();
        range.selectNodeContents(container);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    onChange(container.innerHTML);
  };

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.setAttribute("spellcheck", "false");
      editorRef.current.setAttribute("data-gramm_editor", "false");
      editorRef.current.setAttribute("data-enable-grammarly", "false");
    }
    const removeWebSpellChecker = () => {
      document
        .querySelectorAll('link[href*="webspellchecker"], link[href*="scayt3"]')
        .forEach((el) => el.remove());
    };
    removeWebSpellChecker();

    const observer = new MutationObserver(removeWebSpellChecker);
    observer.observe(document.head, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className="text-editor-container">
        <div className="toolbar">
          <div className="toolbar-group">
            <select
              onChange={(e) => handleFormat("formatBlock", e.target.value)}
              defaultValue=""
            >
              <option value="">Normal</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
            </select>
          </div>

          <div className="toolbar-group">
            <button onClick={() => handleFormat("bold")} title="Bold">
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path
                  d="M6 4h8a4 4 0 010 8H6zM6 12h9a4 4 0 010 8H6z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button onClick={() => handleFormat("italic")} title="Italic">
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path
                  d="M10 4v3h2.21l-3.42 10H6v3h8v-3h-2.21l3.42-10H18V4z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button onClick={() => handleFormat("underline")} title="Underline">
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path
                  d="M6 4v6a6 6 0 0012 0V4h-2v6a4 4 0 01-8 0V4zM4 20v2h16v-2z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>

          <div className="toolbar-group">
            <button
              onClick={() => handleFormat("justifyLeft")}
              title="Align Left"
            >
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path
                  d="M3 4h18v2H3zM3 10h12v2H3zM3 16h18v2H3z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button
              onClick={() => handleFormat("justifyCenter")}
              title="Align Center"
            >
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path
                  d="M6 4h12v2H6zM3 10h18v2H3zM6 16h12v2H6z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button
              onClick={() => handleFormat("justifyRight")}
              title="Align Right"
            >
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path
                  d="M3 4h18v2H3zM9 10h12v2H9zM3 16h18v2H3z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>

          <div className="toolbar-group">
            <button
              onClick={() => handleFormat("insertOrderedList")}
              title="Numbered List"
            >
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path
                  d="M7 6h14v2H7zM7 12h14v2H7zM7 18h14v2H7zM3 6h2V4H3zM3 12h2v-2H3zM3 20h2v-2H3z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button
              onClick={() => handleFormat("insertUnorderedList")}
              title="Bulleted List"
            >
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path
                  d="M4 6h2v2H4zM4 12h2v2H4zM4 18h2v2H4zM8 6h12v2H8zM8 12h12v2H8zM8 18h12v2H8z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>

          <div className="toolbar-group">
            <button onClick={() => insertSymbol("“”")} title="Double Quotes">
              “”
            </button>
            <button onClick={() => insertSymbol("‘’")} title="Single Quotes">
              ‘’
            </button>
            <button onClick={() => insertSymbol("®")} title="Registered">
              ®
            </button>
            <button onClick={() => insertSymbol("©")} title="Copyright">
              ©
            </button>
          </div>

          <div className="toolbar-group">
            <div className="toolbar">
              <button onClick={() => setShowTextColorPicker(true)}>🖌️</button>
              <button onClick={() => setShowBgColorPicker(true)}>🎨</button>
            </div>
          </div>
          <div className="toolbar-group">
            <select
              value={selectedFont}
              onChange={(e) => handleFontChange(e.target.value)}
            >
              <option value="">Font Style</option>
              <option value="Arial">Arial</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Georgia">Georgia</option>
              <option value="Courier New">Courier New</option>
              <option value="Verdana">Verdana</option>
              <option value="Tahoma">Tahoma</option>
              <option value="Trebuchet MS">Trebuchet MS</option>
              <option value="Palatino Linotype">Palatino Linotype</option>
              <option value="Lucida Console">Lucida Console</option>
              <option value="Garamond">Garamond</option>
              <option value="Comic Sans MS">Comic Sans MS</option>
              <option value="Impact">Impact</option>
              <option value="Segoe UI">Segoe UI</option>
              <option value="Calibri">Calibri</option>
              <option value="Cambria">Cambria</option>
              <option value="Franklin Gothic Medium">
                Franklin Gothic Medium
              </option>
              <option value="Book Antiqua">Book Antiqua</option>
              <option value="Century Gothic">Century Gothic</option>
              <option value="Lucida Sans Unicode">Lucida Sans Unicode</option>
            </select>
          </div>

          <button
            onClick={() => {
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                setSavedRange(selection.getRangeAt(0));
              }
              setShowEmojiModal(true);
            }}
            title="Insert Emoji"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "18px",
            }}
          >
            😊
          </button>

          <div className="toolbar-group">
            <button onClick={insertImage} title="Insert Image">
              📷
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                insertVideo();
              }}
              title="Insert Video"
            >
              🎞
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowLinkModal(true);
              }}
              title="Insert Link"
            >
              🔗
            </button>
          </div>

          <button className="source-toggle" onClick={handleToggleSource}>
            {isSource ? "Visual" : "</>"}
          </button>
        </div>

        {!isSource ? (
          <div
            ref={editorRef}
            className="editor-content"
            contentEditable
            onInput={handleInput}
            spellCheck={false}
          />
        ) : (
          <textarea
            className="source-editor"
            value={sourceValue}
            onChange={(e) => {
              setSourceValue(e.target.value);
              onChange(e.target.value);
            }}
          />
        )}
      </div>

      {showImageModal && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 0 20px rgba(0,0,0,0.3)",
            zIndex: 9999,
            width: "300px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              marginBottom: "10px",
              textAlign: "center",
            }}
          >
            Insert Image
          </h3>

          <input
            type="text"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="Enter Image URL"
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "10px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />

          <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
            <input
              type="number"
              value={imageWidth}
              onChange={(e) => setImageWidth(e.target.value)}
              placeholder="Width"
              style={{
                flex: 1,
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                width: "50px",
              }}
            />
            <input
              type="number"
              value={imageHeight}
              onChange={(e) => setImageHeight(e.target.value)}
              placeholder="Height"
              style={{
                flex: 1,
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                width: "50px",
              }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              onClick={() => setShowImageModal(false)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#ccc",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>

            <button
              onClick={handleInsertImage}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#bf7ace",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              Insert
            </button>
          </div>
        </div>
      )}

      {showVideoModal && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 0 20px rgba(0,0,0,0.3)",
            zIndex: 9999,
            width: "300px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              marginBottom: "10px",
              textAlign: "center",
            }}
          >
            Insert Video URL
          </h3>
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="Video URL"
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "10px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              onClick={() => setShowVideoModal(false)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#ccc",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleInsertVideo}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#bf7ace",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              Insert
            </button>
          </div>
        </div>
      )}

      {showLinkModal && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 0 20px rgba(0,0,0,0.3)",
            zIndex: 9999,
            width: "300px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              marginBottom: "10px",
              textAlign: "center",
            }}
          >
            Insert Link
          </h3>
          <input
            type="text"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="Enter Link URL"
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "8px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
          <input
            type="text"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            placeholder="Enter Display Text"
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "12px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              onClick={() => setShowLinkModal(false)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#ccc",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleInsertLink}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#bf7ace",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              Insert
            </button>
          </div>
        </div>
      )}

      {showTextColorPicker && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 0 20px rgba(0,0,0,0.3)",
            zIndex: 9999,
            width: "300px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              marginBottom: "10px",
              textAlign: "center",
            }}
          >
            Pick Text Color
          </h3>
          <input
            type="color"
            value={tempTextColor}
            onChange={(e) => setTempTextColor(e.target.value)}
            style={{ width: "100%", height: "40px", marginBottom: "10px" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              onClick={() => setShowTextColorPicker(false)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#ccc",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (editorRef.current) {
                  document.execCommand("foreColor", false, tempTextColor);
                  onChange(editorRef.current.innerHTML);
                }
                setShowTextColorPicker(false);
              }}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#bf7ace",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
      {showBgColorPicker && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 0 20px rgba(0,0,0,0.3)",
            zIndex: 9999,
            width: "300px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              marginBottom: "10px",
              textAlign: "center",
            }}
          >
            Pick Background Color
          </h3>
          <input
            type="color"
            value={tempBgColor}
            onChange={(e) => setTempBgColor(e.target.value)}
            style={{ width: "100%", height: "40px", marginBottom: "10px" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              onClick={() => setShowBgColorPicker(false)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#ccc",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (editorRef.current) {
                  document.execCommand("hiliteColor", false, tempBgColor);
                  onChange(editorRef.current.innerHTML);
                }
                setShowBgColorPicker(false);
              }}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                background: "#bf7ace",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {showEmojiModal && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 0 20px rgba(0,0,0,0.3)",
            zIndex: 9999,
            width: "320px",
            textAlign: "center",
          }}
        >
          <h3 style={{ fontSize: "16px", marginBottom: "10px" }}>
            Insert Emoji
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: "8px",
              justifyItems: "center",
              fontSize: "22px",
            }}
          >
            {[
              "👍",
              "👎",
              "✔",
              "😀",
              "😂",
              "😍",
              "😎",
              "😢",
              "🤑",
              "🏃‍♂️",
              "⚙",
              "🔐",
              "😡",
              "🤔",
              "😇",
              "🥳",
              "🙄",
              "🤩",
              "👏",
              "🙏",
              "🔥",
              "💯",
              "❤️",
              "🎉",
            ].map((emoji) => (
              <span
                key={emoji}
                style={{ cursor: "pointer" }}
                onClick={() => handleInsertEmoji(emoji)}
              >
                {emoji}
              </span>
            ))}
          </div>
          <button
            onClick={() => setShowEmojiModal(false)}
            style={{
              marginTop: "15px",
              padding: "8px 12px",
              borderRadius: "6px",
              background: "#ccc",
              border: "none",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      )}
    </>
  );
};

export default QuillEditor;
